import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
    appEndpoint,
    orgEndpoint,
    releaseEndpoint,
    stepConfigEndpoint,
    stepRunEndpoint,
    workflowEndpoint,
    workflowRunEndpoint,
} from '@/helpers/apiHelpers';
import {
    AutomationType,
    AppHome,
    AppSettings,
    AppStoreAvailableBuild,
    BuildDistroBuild,
    BuildDistroSettings,
    IntegrationInstallationId,
    SettingField,
    TriageSettings,
    OrgBuildDistroBucketsResponse,
    OrgOverviewAvailableMetricsResponse,
    OrgOverviewV2,
    ReleaseType,
    RolloutGroupSummary,
    RunwayModelsResponse,
    RunwayResponseType,
    type RunwayResponseV2,
    TimelineEvent,
    TimelineEventType,
    TriageTicketFilterGroup,
    UserEmailNotificationSettings,
} from '@/types';
import { type BaseAPIResponse, baseQuery } from './common';
import qs from 'query-string';
import type { GetPendingAssociationTicketRequest, GetPendingAssociationTicketResponse } from '@/actions/releaseStep';
import type {
    OrgInsightsResponse,
    OrgOverviewEntityRequest,
    OrgOverviewFiltersV2,
    WorkflowOverviewEntityRequest,
} from '@/actions/entities';
import type { WorkflowOverview } from '@/types/workflowOverview';
import type {
    DependenciesFileOptionsRequest,
    DependenciesFileOptionsResponse,
    DependenciesReleaseResponse,
    DependenciesRequest,
    SaveDependenciesConfigRequest,
} from '@/types/dependencies';
import type { RootState } from '@/reducers/reducers';
import { updateStepRuns } from '@/actions/stepRun';
import { stepRunsSummaryActions } from '@/reducers/stepRunsSummary';
import type {
    ApproveHotfixRequestBody,
    CreateHotfixRequestBody,
    HotfixRequest,
    HotfixRequestDetail,
    HotfixRequestMutationScope,
    RejectHotfixRequestBody,
    HotfixRequestSafeError,
    HotfixRequestScope,
    HotfixRequestSettings,
    UpdateHotfixRequestSettingsBody,
    UpdateHotfixRequestBody,
} from '@/types/hotfixRequests';

export interface GetStepRunsSummaryRequest {
    orgId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunIds: string[];
}

export interface GetRetainedBuildOptionsRequest {
    orgId: string;
    workflowId: string;
    stepConfigId: string;
}

export interface RetainedBuildOptions {
    availableBuilds: AppStoreAvailableBuild[];
    selectedRetainedBuildIds: string[];
}

export interface RetryAutomationRequest {
    orgId: string;
    appId: string;
    releaseId?: string;
    workflowId: string;
    workflowRunId: string;
    stepRunId: string;
    automationRunId: string;
    automationType?: AutomationType;
    eventData?: TimelineEvent['eventData'];
    eventType?: TimelineEventType;
    isAutoExecEvent?: boolean;
}

const HOTFIX_SUPPORTED_RETRYABLE_AUTOMATIONS = [
    AutomationType.AutoKickoffRelease,
    AutomationType.AutoCherryPickFromWorkingBranch,
    AutomationType.AutoBumpVersion,
];

export interface AppliedChange {
    path: string;
    action: 'create' | 'update' | 'delete';
    removedLines: string[];
    addedLines: string[];
}

export interface AppliedPlan {
    planId: string;
    appId: string;
    appliedBy: string;
    appliedAt: string;
    changes: AppliedChange[];
}

export interface PaginatedAppliedPlans {
    plans: AppliedPlan[];
    paginationInfo: { nextCursor?: string; hasMore: boolean };
}

export interface GetConfigAsCodeAppliedPlansRequest {
    orgId: string;
    appId?: string;
    planId?: string;
    cursor?: string;
}

type BaseRequest = {
    orgId: string;
    appId: string;
};

export function unwrapHotfixRequestResponse<T>(
    response: RunwayResponseV2<T>,
): { data: T } | { error: FetchBaseQueryError } {
    if (response.type === RunwayResponseType.Error || response.data === undefined) {
        const message = response.message ?? 'Unable to process hotfix request. Please try again.';
        const safeError: HotfixRequestSafeError = { message };
        return { error: { status: 'CUSTOM_ERROR', error: message, data: safeError } };
    }
    return { data: response.data };
}

export function hotfixRequestErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'data' in error) {
        const data = error.data;
        if (typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string') {
            return data.message;
        }
    }
    return 'Unable to process hotfix request. Please try again.';
}

const hotfixRequestsUrl = ({ orgId, appId }: HotfixRequestScope) => `${appEndpoint(orgId, appId)}/hotfixRequests`;
const hotfixRequestsTag = ({ orgId, appId }: HotfixRequestScope) => ({
    type: 'HotfixRequests' as const,
    id: `${orgId}-${appId}`,
});
const hotfixRequestSettingsUrl = ({ orgId, appId }: HotfixRequestScope) =>
    `${appEndpoint(orgId, appId)}/hotfixRequestSettings`;
const hotfixRequestSettingsTag = ({ orgId, appId }: HotfixRequestScope) => ({
    type: 'HotfixRequestSettings' as const,
    id: `${orgId}-${appId}`,
});

async function hotfixRequestMutation<T extends HotfixRequestMutationScope>(
    request: T,
    path: string,
    body: unknown,
    fetchWithBQ: (request: Parameters<typeof baseQuery>[0]) => ReturnType<typeof baseQuery>,
) {
    const response = await fetchWithBQ({
        url: `${hotfixRequestsUrl(request)}/${encodeURIComponent(request.requestId)}${path}`,
        method: 'POST',
        body,
    });
    if (response.error) return { error: response.error };
    return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequestDetail>);
}

export const defaultApi = createApi({
    reducerPath: 'defaultApi',
    baseQuery: baseQuery,
    tagTypes: [
        'AppHome',
        'PendingAssociationTickets',
        'WorkflowOverview',
        'StepRunsSummary',
        'OrgOverview',
        'UserEmailNotificationSettings',
        'HotfixRequests',
        'HotfixRequestSettings',
        'Dependencies',
        'IntegrationSettingFields',
        'NotificationSettingFields',
        'AutomationSettingFields',
        'StepConfigSettingFields',
    ],
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getDependencies: build.query<BaseAPIResponse<DependenciesReleaseResponse>, DependenciesRequest>({
            query: ({ orgId, workflowId, workflowRunId }) =>
                `${workflowRunEndpoint({ orgId, workflowId, workflowRunId })}/dependencies`,
            providesTags: (_result, _error, { workflowRunId }) => [{ type: 'Dependencies', id: workflowRunId ?? '' }],
        }),
        getDependenciesFileOptions: build.query<
            BaseAPIResponse<DependenciesFileOptionsResponse>,
            DependenciesFileOptionsRequest
        >({
            query: ({ orgId, workflowId, workflowRunId, destinationId }) => ({
                url: `${workflowRunEndpoint({ orgId, workflowId, workflowRunId })}/dependencies/fileOptions`,
                params: { destinationId },
            }),
            // Walking the repo tree costs a VCS round trip, so hold it for the length of a
            // configure session rather than refetching every time the modal opens.
            keepUnusedDataFor: 5 * 60,
        }),
        saveDependenciesConfig: build.mutation<
            BaseAPIResponse<DependenciesReleaseResponse>,
            SaveDependenciesConfigRequest
        >({
            query: ({ orgId, workflowId, workflowRunId, destinationId, ios, android }) => ({
                url: `${workflowRunEndpoint({ orgId, workflowId, workflowRunId })}/dependencies/config`,
                method: 'POST',
                body: { destinationId, ios, android },
            }),
            // The save returns the full set of reports, so write them straight into the cache. An
            // invalidation would refetch every destination just to learn what we were already told.
            async onQueryStarted({ orgId, workflowId, workflowRunId }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        defaultApi.util.upsertQueryData('getDependencies', { orgId, workflowId, workflowRunId }, data),
                    );
                } catch {
                    // Leave the cache alone: the next mount refetches it.
                }
            },
        }),
        getAppHome: build.query<BaseAPIResponse<AppHome>, BaseRequest>({
            query: ({ orgId, appId }) => {
                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/appHome`;
            },
            providesTags: (_result, _error, { orgId, appId }) => [{ type: 'AppHome', id: `${orgId}-${appId}` }],
        }),
        getActiveHotfixRequests: build.query<HotfixRequest[], HotfixRequestScope>({
            async queryFn(scope, _api, _options, fetchWithBQ) {
                const response = await fetchWithBQ(hotfixRequestsUrl(scope));
                if (response.error) return { error: response.error };
                return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequest[]>);
            },
            providesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        getHotfixRequestSettings: build.query<HotfixRequestSettings, HotfixRequestScope>({
            async queryFn(scope, _api, _options, fetchWithBQ) {
                const response = await fetchWithBQ(hotfixRequestSettingsUrl(scope));
                if (response.error) return { error: response.error };
                return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequestSettings>);
            },
            providesTags: (_result, _error, scope) => [hotfixRequestSettingsTag(scope)],
        }),
        updateHotfixRequestSettings: build.mutation<HotfixRequestSettings, UpdateHotfixRequestSettingsBody>({
            async queryFn({ orgId, appId, ...body }, _api, _options, fetchWithBQ) {
                const scope = { orgId, appId };
                const response = await fetchWithBQ({
                    url: hotfixRequestSettingsUrl(scope),
                    method: 'PUT',
                    body,
                });
                if (response.error) return { error: response.error };
                return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequestSettings>);
            },
            invalidatesTags: (_result, _error, scope) => [hotfixRequestSettingsTag(scope)],
        }),
        getHotfixRequest: build.query<HotfixRequestDetail, HotfixRequestScope & { requestId: string }>({
            async queryFn(scope, _api, _options, fetchWithBQ) {
                const response = await fetchWithBQ(
                    `${hotfixRequestsUrl(scope)}/${encodeURIComponent(scope.requestId)}`,
                );
                if (response.error) return { error: response.error };
                return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequestDetail>);
            },
            providesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        createHotfixRequest: build.mutation<HotfixRequestDetail, HotfixRequestScope & CreateHotfixRequestBody>({
            async queryFn({ proposal, justification, formFieldValues, ...scope }, _api, _options, fetchWithBQ) {
                const response = await fetchWithBQ({
                    url: hotfixRequestsUrl(scope),
                    method: 'POST',
                    body: { proposal, justification, formFieldValues },
                });
                if (response.error) return { error: response.error };
                return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequestDetail>);
            },
            // Invalidate on errors too: conflicts must refresh state without replaying the POST.
            invalidatesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        updateHotfixRequest: build.mutation<HotfixRequestDetail, UpdateHotfixRequestBody>({
            async queryFn(
                { expectedRevision, proposal, justification, formFieldValues, ...scope },
                _api,
                _options,
                fetchWithBQ,
            ) {
                const response = await fetchWithBQ({
                    url: `${hotfixRequestsUrl(scope)}/${encodeURIComponent(scope.requestId)}`,
                    method: 'PUT',
                    body: { expectedRevision, proposal, justification, formFieldValues },
                });
                if (response.error) return { error: response.error };
                return unwrapHotfixRequestResponse(response.data as RunwayResponseV2<HotfixRequestDetail>);
            },
            invalidatesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        rejectHotfixRequest: build.mutation<HotfixRequestDetail, RejectHotfixRequestBody>({
            queryFn(request, _api, _options, fetchWithBQ) {
                return hotfixRequestMutation(request, '/reject', { reason: request.reason }, fetchWithBQ);
            },
            invalidatesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        cancelHotfixRequest: build.mutation<HotfixRequestDetail, HotfixRequestMutationScope>({
            queryFn(request, _api, _options, fetchWithBQ) {
                return hotfixRequestMutation(request, '/cancel', {}, fetchWithBQ);
            },
            invalidatesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        approveHotfixRequest: build.mutation<HotfixRequestDetail, ApproveHotfixRequestBody>({
            queryFn(request, _api, _options, fetchWithBQ) {
                return hotfixRequestMutation(
                    request,
                    '/approve',
                    { expectedRevision: request.expectedRevision, finalProposal: request.finalProposal },
                    fetchWithBQ,
                );
            },
            invalidatesTags: (_result, _error, scope) => [hotfixRequestsTag(scope)],
        }),
        getIntegrationSettingFields: build.query<
            BaseAPIResponse<Record<IntegrationInstallationId, SettingField[]>>,
            BaseRequest
        >({
            query: ({ orgId, appId }) => `${appEndpoint(orgId, appId)}/integrations/settingFields`,
            providesTags: (_result, _error, { orgId, appId }) => [
                { type: 'IntegrationSettingFields', id: `${orgId}-${appId}` },
            ],
        }),
        getNotificationSettingFields: build.query<BaseAPIResponse<Record<string, SettingField[]>>, BaseRequest>({
            query: ({ orgId, appId }) => `${appEndpoint(orgId, appId)}/notifications/settingFields`,
            providesTags: (_result, _error, { orgId, appId }) => [
                { type: 'NotificationSettingFields', id: `${orgId}-${appId}` },
            ],
        }),
        getAutomationSettingFields: build.query<BaseAPIResponse<Record<string, SettingField[]>>, BaseRequest>({
            query: ({ orgId, appId }) => `${appEndpoint(orgId, appId)}/automations/settingFields`,
            providesTags: ['AutomationSettingFields'],
        }),
        getWorkflowStepConfigSettingFields: build.query<
            BaseAPIResponse<Record<string, SettingField[]>>,
            { orgId: string; workflowId: string; stepConfigId?: string }
        >({
            query: ({ orgId, workflowId, stepConfigId }) => ({
                url: `${workflowEndpoint({ orgId, workflowId })}/stepConfigs/settingFields`,
                params: { stepConfigId },
            }),
            providesTags: ['StepConfigSettingFields'],
        }),
        getRetainedBuildOptions: build.query<BaseAPIResponse<RetainedBuildOptions>, GetRetainedBuildOptionsRequest>({
            query: ({ orgId, workflowId, stepConfigId }) =>
                `${stepConfigEndpoint({ orgId, workflowId, stepConfigId })}/retainedBuildOptions`,
            keepUnusedDataFor: 60,
        }),
        getIssueTrackingLabels: build.query<BaseAPIResponse<{ labels: string[] }>, BaseRequest>({
            query: ({ orgId, appId }) => {
                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/issueTrackingLabels`;
            },
            keepUnusedDataFor: 3 * 60,
        }),
        getOrgOverview: build.query<BaseAPIResponse<OrgOverviewV2>, OrgOverviewEntityRequest<OrgOverviewFiltersV2>>({
            query: ({ orgId, filters = {} }) => {
                const orgUrl = orgEndpoint(orgId);
                return {
                    url: `${orgUrl}/overview`,
                    method: 'POST',
                    body: {
                        ...filters,
                    },
                };
            },
            providesTags: (_result, _error, { orgId, appIds, filters }) => [
                {
                    type: 'OrgOverview',
                    id: `${orgId}-${appIds.join(',')}-${filters?.startDate ?? ''}-${filters?.endDate ?? ''}`,
                },
            ],
        }),
        getConfigAsCodeAppliedPlans: build.query<
            BaseAPIResponse<PaginatedAppliedPlans>,
            GetConfigAsCodeAppliedPlansRequest
        >({
            query: ({ orgId, appId, planId, cursor }) => ({
                url: `${orgEndpoint(orgId)}/configAsCode/plans`,
                params: { appId, planId, cursor },
            }),
        }),
        getOrgInsights: build.query<BaseAPIResponse<OrgInsightsResponse>, { orgId: string }>({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/insights`;
            },
        }),
        getOrgAvailableMetrics: build.query<BaseAPIResponse<OrgOverviewAvailableMetricsResponse>, { orgId: string }>({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/availableMetrics`;
            },
        }),
        getOrgBuildDistroBuckets: build.query<BaseAPIResponse<OrgBuildDistroBucketsResponse>, { orgId: string }>({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/buildDistro/buckets`;
            },
        }),
        updateBuildDistroSettings: build.mutation<
            BaseAPIResponse<BuildDistroSettings>,
            { orgId: string; settings: BuildDistroSettings }
        >({
            query: ({ orgId, settings }) => {
                const orgUrl = orgEndpoint(orgId);
                return {
                    url: `${orgUrl}/updateBuildDistroSettings`,
                    method: 'POST',
                    body: settings,
                };
            },
        }),
        updateTriageSettings: build.mutation<
            BaseAPIResponse<AppSettings>,
            { orgId: string; appId: string; settings: TriageSettings }
        >({
            query: ({ orgId, appId, settings }) => {
                const appUrl = appEndpoint(orgId, appId);
                return {
                    url: `${appUrl}/triageSettings`,
                    method: 'PUT',
                    body: settings,
                };
            },
        }),
        updateTriageTicketFilters: build.mutation<
            BaseAPIResponse<AppSettings>,
            {
                orgId: string;
                appId: string;
                conditionGroups: TriageTicketFilterGroup[];
                closeNonMatchingIssues: boolean;
            }
        >({
            query: ({ orgId, appId, conditionGroups, closeNonMatchingIssues }) => {
                const appUrl = appEndpoint(orgId, appId);
                return {
                    url: `${appUrl}/triageTicketFilters`,
                    method: 'PUT',
                    body: { conditionGroups, closeNonMatchingIssues },
                };
            },
        }),
        toggleExternalBuildLinkEnabled: build.mutation<
            BaseAPIResponse<BuildDistroBuild>,
            { orgId: string; appId: string; bucketId: string; buildId: string; publicBuildLinkEnabled: boolean }
        >({
            query: ({ orgId, appId, bucketId, buildId, publicBuildLinkEnabled }) => {
                const appUrl = appEndpoint(orgId, appId);
                return {
                    url: `${appUrl}/build-distribution/bucket/${bucketId}/build/${buildId}/toggleExternalBuildLinkEnabled`,
                    method: 'POST',
                    body: { publicBuildLinkEnabled },
                };
            },
        }),
        getUserEmailNotificationSettings: build.query<
            BaseAPIResponse<UserEmailNotificationSettings>,
            { orgId: string }
        >({
            query: ({ orgId }) => {
                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/userEmailNotificationSettings`;
            },
            providesTags: (_result, _error, { orgId }) => [{ type: 'UserEmailNotificationSettings', id: orgId }],
        }),
        updateUserEmailNotificationSettings: build.mutation<
            BaseAPIResponse<null>,
            { orgId: string; settings: UserEmailNotificationSettings }
        >({
            query: ({ orgId, settings }) => {
                const orgUrl = orgEndpoint(orgId);
                return {
                    url: `${orgUrl}/userEmailNotificationSettings`,
                    method: 'POST',
                    body: { emailNotificationSettings: settings },
                };
            },
            invalidatesTags: (_result, _error, { orgId }) => [{ type: 'UserEmailNotificationSettings', id: orgId }],
        }),
        getPendingAssociationTickets: build.query<
            GetPendingAssociationTicketResponse,
            GetPendingAssociationTicketRequest
        >({
            query: ({ orgId, appId, releaseId, searchCriteria }) => {
                const encodedQueryParams = qs.stringify({ searchCriteria }, { skipNull: true, skipEmptyString: true });
                const releaseUrl = releaseEndpoint(orgId, appId, releaseId);
                return `${releaseUrl}/ticketsPendingAssociation?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, appId, releaseId }) => [
                { type: 'PendingAssociationTickets', id: `${orgId}-${appId}-${releaseId}` },
            ],
        }),
        getWorkflowOverview: build.query<BaseAPIResponse<WorkflowOverview>, WorkflowOverviewEntityRequest>({
            query: ({ orgId, workflowId, destinationStepIds, excludedDestinationStepIds }) => {
                const params: Record<string, string[]> = {};
                if (destinationStepIds?.length) params.destinationStepIds = destinationStepIds;
                if (excludedDestinationStepIds?.length) params.excludedDestinationStepIds = excludedDestinationStepIds;
                const encodedQueryParams = qs.stringify(params);
                const workflowUrl = workflowEndpoint({ orgId, workflowId });
                return encodedQueryParams ? `${workflowUrl}/overview?${encodedQueryParams}` : `${workflowUrl}/overview`;
            },
            providesTags: (_result, _error, { workflowId }) => [{ type: 'WorkflowOverview', id: workflowId }],
        }),
        getStepRunsSummary: build.query<RunwayModelsResponse, GetStepRunsSummaryRequest>({
            query: ({ orgId, workflowId, workflowRunId, stepRunIds }) => {
                const base = workflowRunEndpoint({ orgId, workflowId, workflowRunId });
                const queryParams = qs.stringify({ stepRunIds: stepRunIds.join(',') });
                return `${base}/stepGroupSummary?${queryParams}`;
            },
            keepUnusedDataFor: 3 * 60,
            transformResponse: (response: BaseAPIResponse<RunwayModelsResponse>) => response.data,
            providesTags: (_result, _error, { workflowRunId, stepRunIds }) => [
                { type: 'StepRunsSummary', id: `${workflowRunId}-${stepRunIds.join(',')}` },
            ],
        }),
        getRolloutGroupSummary: build.query<RolloutGroupSummary, GetStepRunsSummaryRequest>({
            query: ({ orgId, workflowId, workflowRunId, stepRunIds }) => ({
                url: `${workflowRunEndpoint({ orgId, workflowId, workflowRunId })}/rolloutGroupSummary`,
                method: 'POST',
                body: { stepRunIds },
            }),
            keepUnusedDataFor: 3 * 60,
            transformResponse: (response: BaseAPIResponse<RolloutGroupSummary>) => response.data,
        }),
        retryAutomation: build.mutation<BaseAPIResponse<RunwayModelsResponse>, RetryAutomationRequest>({
            queryFn: async (request, api, _extraOptions, fetchWithBQ) => {
                const state = api.getState() as RootState;
                const release = request.releaseId ? state.entities.releases.byId[request.releaseId] : null;
                const shouldUseHotfixRetryEndpoint =
                    release?.type === ReleaseType.Hotfix &&
                    !!request.automationType &&
                    HOTFIX_SUPPORTED_RETRYABLE_AUTOMATIONS.includes(request.automationType) &&
                    !request.isAutoExecEvent;

                const url =
                    shouldUseHotfixRetryEndpoint && request.releaseId ?
                        `${releaseEndpoint(request.orgId, request.appId, request.releaseId)}/automation/retryHotfixAutomation`
                    :   `${stepRunEndpoint(request)}/retryAutomation/${request.automationRunId}`;

                const result = await fetchWithBQ({
                    url,
                    method: 'POST',
                    body:
                        shouldUseHotfixRetryEndpoint ?
                            {
                                automationType: request.automationType,
                                eventData: request.eventData,
                                eventType: request.eventType,
                            }
                        :   {
                                automationRunId: request.automationRunId,
                            },
                });

                if (result.error) {
                    return { error: result.error };
                }

                return { data: result.data as BaseAPIResponse<RunwayModelsResponse> };
            },
            async onQueryStarted(_request, { dispatch, queryFulfilled }) {
                let response: BaseAPIResponse<RunwayModelsResponse>;
                try {
                    ({ data: response } = await queryFulfilled);
                } catch {
                    return;
                }

                if (response.data?.stepRuns) {
                    dispatch(updateStepRuns(response.data.stepRuns));
                    response.data.stepRuns.forEach((stepRun) => {
                        dispatch(stepRunsSummaryActions.updateStepRun(stepRun));
                    });
                }
            },
        }),
    }),
});

export const invalidateAppHomeCache = (args: { orgId: string; appId: string }) => {
    return defaultApi.util.invalidateTags([{ type: 'AppHome', id: `${args.orgId}-${args.appId}` }]);
};

// Fields' options and availability depend on the app's integrations
export const invalidateSettingFieldsCaches = () => {
    return defaultApi.util.invalidateTags([
        'IntegrationSettingFields',
        'NotificationSettingFields',
        'AutomationSettingFields',
        'StepConfigSettingFields',
    ]);
};

export const invalidateWorkflowOverviewCache = (args: { workflowId: string }) => {
    return defaultApi.util.invalidateTags([{ type: 'WorkflowOverview', id: args.workflowId }]);
};

export const {
    useApproveHotfixRequestMutation,
    useCancelHotfixRequestMutation,
    useCreateHotfixRequestMutation,
    useUpdateHotfixRequestMutation,
    useGetHotfixRequestSettingsQuery,
    useUpdateHotfixRequestSettingsMutation,
    useGetActiveHotfixRequestsQuery,
    useGetAppHomeQuery,
    useGetConfigAsCodeAppliedPlansQuery,
    useGetIntegrationSettingFieldsQuery,
    useGetNotificationSettingFieldsQuery,
    useGetAutomationSettingFieldsQuery,
    useGetWorkflowStepConfigSettingFieldsQuery,
    useGetDependenciesQuery,
    useGetDependenciesFileOptionsQuery,
    useSaveDependenciesConfigMutation,
    useGetIssueTrackingLabelsQuery,
    useGetHotfixRequestQuery,
    useGetOrgAvailableMetricsQuery,
    useGetOrgBuildDistroBucketsQuery,
    useLazyGetOrgInsightsQuery,
    useLazyGetOrgOverviewQuery,
    useGetPendingAssociationTicketsQuery,
    useGetRetainedBuildOptionsQuery,
    useGetUserEmailNotificationSettingsQuery,
    useUpdateUserEmailNotificationSettingsMutation,
    useGetWorkflowOverviewQuery,
    useLazyGetStepRunsSummaryQuery,
    useGetRolloutGroupSummaryQuery,
    useRetryAutomationMutation,
    useRejectHotfixRequestMutation,
    useUpdateBuildDistroSettingsMutation,
    useUpdateTriageSettingsMutation,
    useUpdateTriageTicketFiltersMutation,
    useToggleExternalBuildLinkEnabledMutation,
} = defaultApi;
